import { DOCUMENT } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { APP_CONSTANTS } from "../constants/app.constants";

export interface SeoConfig {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  noIndex?: boolean;
  /** Pass the slug (without locale prefix) to emit hreflang alternates. Empty string = root. Omit = no hreflang. */
  hreflangSlug?: string;
}

const LOCALES = ["es", "de", "fr", "it", "pt-br", "ja"] as const;
const HREFLANG: Record<string, string> = {
  es: "es",
  de: "de",
  fr: "fr",
  it: "it",
  "pt-br": "pt-BR",
  ja: "ja",
};

@Injectable({ providedIn: "root" })
export class SeoService {
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);

  updatePage(config: SeoConfig): void {
    const title = config.title;
    const description = config.description;
    const ogTitle = config.ogTitle ?? title;
    const ogDescription = config.ogDescription ?? description;
    const ogImage = config.ogImage ?? APP_CONSTANTS.defaultOgImage;

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: "description", content: description });
    this.meta.updateTag({
      name: "robots",
      content: config.noIndex ? "noindex, nofollow" : "index, follow",
    });
    this.meta.updateTag({ property: "og:title", content: ogTitle });
    this.meta.updateTag({ property: "og:description", content: ogDescription });
    this.meta.updateTag({ property: "og:url", content: config.canonical });
    this.meta.updateTag({ property: "og:image", content: ogImage });
    this.meta.updateTag({ name: "twitter:title", content: ogTitle });
    this.meta.updateTag({
      name: "twitter:description",
      content: ogDescription,
    });
    this.meta.updateTag({ name: "twitter:image", content: ogImage });
    this.setCanonical(config.canonical);
    this.removePageJsonLd();

    if (config.hreflangSlug !== undefined) {
      this.setHreflang(config.hreflangSlug);
    } else {
      this.removeHreflang();
    }
  }

  /** Inject page-specific JSON-LD (e.g. FAQPage). Replaced on every navigation. */
  setPageJsonLd(data: Record<string, unknown>): void {
    let script = this.document.querySelector<HTMLScriptElement>(
      'script[data-ld="page"]',
    );
    if (!script) {
      script = this.document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-ld", "page");
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  private removePageJsonLd(): void {
    this.document.querySelector('script[data-ld="page"]')?.remove();
  }

  private setHreflang(slug: string): void {
    this.removeHreflang();
    const base = APP_CONSTANTS.siteUrl;
    const enUrl = slug ? `${base}/${slug}/` : `${base}/`;

    const entries: { hreflang: string; href: string }[] = [
      { hreflang: "x-default", href: enUrl },
      { hreflang: "en", href: enUrl },
    ];

    for (const locale of LOCALES) {
      entries.push({
        hreflang: HREFLANG[locale],
        href: slug ? `${base}/${locale}/${slug}/` : `${base}/${locale}/`,
      });
    }

    for (const { hreflang, href } of entries) {
      const link = this.document.createElement("link");
      link.rel = "alternate";
      link.setAttribute("hreflang", hreflang);
      link.href = href;
      link.setAttribute("data-hreflang", "true");
      this.document.head.appendChild(link);
    }
  }

  private removeHreflang(): void {
    this.document
      .querySelectorAll("link[data-hreflang]")
      .forEach((el) => el.remove());
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!link) {
      link = this.document.createElement("link");
      link.rel = "canonical";
      this.document.head.appendChild(link);
    }
    link.href = url;
  }
}
